import { createContext, useContext, useEffect, useState } from 'react'
import { createContextualCan } from '@casl/react'
import { axiosDZAuthInstance } from '@/services/api/axios'
import { updateAbility } from './ability'
import { AppAbility } from '@/services/casl/defineAbility'
import { PureAbility } from '@casl/ability'
import { useAuth } from './AuthContext'

export interface Permission {
  subject: string
  action: string
}

interface AbilityProviderProps {
  children: React.ReactNode
}

export const AbilityContext = createContext<AppAbility | undefined>(undefined)
export const Can = createContextualCan<AppAbility>(AbilityContext.Consumer as React.Consumer<AppAbility>)

// Hook to access the AbilityContext
export const useAbility = (): AppAbility => {
  const context = useContext(AbilityContext)
  if (!context) {
    throw new Error('useAbility must be used within an AbilityProvider')
  }
  return context
}

export const AbilityProvider = ({ children }: AbilityProviderProps) => {
  const [ability] = useState(() => new PureAbility<[string, string]>())
  const [loading, setLoading] = useState(false)

  const { tokenParsed } = useAuth()

  const { userId: adminId } = tokenParsed || {}

  useEffect(() => {
    if (!adminId) return

    const fetchUserPermissions = async (adminId: string) => {
      setLoading(true)
      try {
        const response = await axiosDZAuthInstance.get<{ userPermissions: Permission[] }>(
          `/dashboard/admins/${adminId}/permissions`,
        )

        const rules = updateAbility(response.data.userPermissions)
        ability.update(rules)
      } catch (err) {
        console.log('Failed to fetch user permissions')
      } finally {
        setLoading(false)
      }
    }

    fetchUserPermissions(adminId)
  }, [])

  if (loading) {
    return null
  }

  return <AbilityContext.Provider value={ability}>{children}</AbilityContext.Provider>
}
